import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    JOURNEY_BREAKPOINTS,
    formatPointsExport,
    getDefaultPoints,
} from './journeyPointsConfig';
import './JourneyDevPanel.css';

const PANEL_POS_KEY = 'dv2JourneyDevPanelPos';

function readPanelPos() {
    try {
        const raw = window.localStorage?.getItem(PANEL_POS_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (
            parsed &&
            Number.isFinite(parsed.x) &&
            Number.isFinite(parsed.y)
        ) {
            return { x: parsed.x, y: parsed.y };
        }
    } catch {
        /* ignore */
    }
    return null;
}

function writePanelPos(pos) {
    try {
        window.localStorage?.setItem(PANEL_POS_KEY, JSON.stringify(pos));
    } catch {
        /* ignore */
    }
}

/**
 * Floating developer panel for tuning mountain journey coordinates.
 * Only mounted when journey dev mode is available (non-production).
 */
export default function JourneyDevPanel({
    enabled,
    onToggleEnabled,
    breakpointId,
    viewportWidth,
    cardSize,
    points,
    pathSamples,
    overrides,
    drawMode,
    drawStatus,
    livePointCount = 0,
    onToggleDrawMode,
    onFinishDrawnPath,
    onUndoDrawPoint,
    onClearPath,
    onPlaceMarkersOnPath,
    onResetBreakpoint,
    onResetAll,
    onSideChange,
    onPointNudge,
}) {
    const [copied, setCopied] = useState('');
    const [collapsed, setCollapsed] = useState(false);
    const [pos, setPos] = useState(() =>
        typeof window !== 'undefined' ? readPanelPos() : null
    );
    const dragRef = useRef(null);
    const panelRef = useRef(null);

    const exportText = useMemo(
        () => formatPointsExport(overrides),
        [overrides]
    );

    const currentSnippet = useMemo(() => {
        const rows = (points || []).map((p) => {
            const type = p.type === 'oolaOne' ? `, type: 'oolaOne'` : '';
            return `        { rank: ${p.rank}${type}, x: ${p.x}, y: ${p.y}, side: '${p.side}' },`;
        });
        const sampleRows = (pathSamples || [])
            .map((p) => `        { x: ${p.x}, y: ${p.y} },`)
            .join('\n');
        return [
            `    // points — ${breakpointId}`,
            `    ${breakpointId}: [`,
            rows.join('\n'),
            `    ],`,
            pathSamples?.length
                ? [
                      `    // pathSamples — ${breakpointId} (${pathSamples.length} pts)`,
                      `    ${breakpointId}: [`,
                      sampleRows,
                      `    ],`,
                  ].join('\n')
                : `    // pathSamples — ${breakpointId}: null`,
        ].join('\n');
    }, [points, pathSamples, breakpointId]);

    const copy = async (label, text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(label);
            window.setTimeout(() => setCopied(''), 1600);
        } catch {
            setCopied('failed');
            window.setTimeout(() => setCopied(''), 1600);
        }
    };

    const clampPos = useCallback((x, y) => {
        const el = panelRef.current;
        const w = el?.offsetWidth || 340;
        const h = el?.offsetHeight || 200;
        const maxX = Math.max(8, window.innerWidth - w - 8);
        const maxY = Math.max(8, window.innerHeight - Math.min(h, 120) - 8);
        return {
            x: Math.min(maxX, Math.max(8, x)),
            y: Math.min(maxY, Math.max(8, y)),
        };
    }, []);

    const onDragHandleDown = useCallback(
        (e) => {
            if (e.button != null && e.button !== 0) return;
            // Don't start drag from action buttons
            if (e.target.closest('button, select, input, a')) return;
            e.preventDefault();
            const start = pos || {
                x: window.innerWidth - 352,
                y: 72,
            };
            dragRef.current = {
                originX: e.clientX,
                originY: e.clientY,
                startX: start.x,
                startY: start.y,
            };

            const onMove = (ev) => {
                if (!dragRef.current) return;
                const dx = ev.clientX - dragRef.current.originX;
                const dy = ev.clientY - dragRef.current.originY;
                const next = clampPos(
                    dragRef.current.startX + dx,
                    dragRef.current.startY + dy
                );
                setPos(next);
            };

            const onUp = () => {
                window.removeEventListener('pointermove', onMove);
                window.removeEventListener('pointerup', onUp);
                window.removeEventListener('pointercancel', onUp);
                dragRef.current = null;
                setPos((current) => {
                    if (!current) return current;
                    writePanelPos(current);
                    return current;
                });
            };

            window.addEventListener('pointermove', onMove);
            window.addEventListener('pointerup', onUp);
            window.addEventListener('pointercancel', onUp);
        },
        [clampPos, pos]
    );

    useEffect(() => {
        if (!pos) return undefined;
        const onResize = () => {
            setPos((current) => {
                if (!current) return current;
                const next = clampPos(current.x, current.y);
                writePanelPos(next);
                return next;
            });
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [clampPos, pos]);

    if (!enabled) {
        return (
            <button
                type="button"
                className="dv2-journey-dev__fab"
                onClick={() => onToggleEnabled(true)}
                title="Enable Journey Dev Mode"
            >
                Journey Dev
            </button>
        );
    }

    const bp = JOURNEY_BREAKPOINTS[breakpointId] || JOURNEY_BREAKPOINTS.desktop;
    const defaults = getDefaultPoints(breakpointId);
    const dirty =
        JSON.stringify(points) !== JSON.stringify(defaults) ||
        Boolean(overrides[breakpointId]) ||
        Boolean(pathSamples?.length);

    const panelStyle = pos
        ? { top: pos.y, left: pos.x, right: 'auto' }
        : undefined;

    return (
        <aside
            ref={panelRef}
            className={`dv2-journey-dev${collapsed ? ' is-collapsed' : ''}${
                pos ? ' is-positioned' : ''
            }`}
            style={panelStyle}
            aria-label="Journey developer panel"
        >
            <header
                className="dv2-journey-dev__head"
                onPointerDown={onDragHandleDown}
                title="Drag to move"
            >
                <div className="dv2-journey-dev__drag-label">
                    <span className="dv2-journey-dev__grip" aria-hidden="true">
                        ⋮⋮
                    </span>
                    <strong>Journey Dev</strong>
                    <span className="dv2-journey-dev__badge">local only</span>
                </div>
                <div className="dv2-journey-dev__head-actions">
                    <button
                        type="button"
                        className="dv2-journey-dev__icon-btn"
                        onClick={() => setCollapsed((v) => !v)}
                    >
                        {collapsed ? 'Expand' : 'Collapse'}
                    </button>
                    <button
                        type="button"
                        className="dv2-journey-dev__icon-btn"
                        onClick={() => onToggleEnabled(false)}
                    >
                        Off
                    </button>
                </div>
            </header>

            {!collapsed ? (
                <div className="dv2-journey-dev__body">
                    <p className="dv2-journey-dev__hint">
                        Draw path with clicks, Finish path, then use{' '}
                        <strong>Copy all breakpoints JSON</strong> and paste it
                        in chat.
                    </p>

                    <div className="dv2-journey-dev__draw-row">
                        <button
                            type="button"
                            className={`dv2-journey-dev__draw-btn${
                                drawMode ? ' is-on' : ''
                            }`}
                            onClick={() => onToggleDrawMode(!drawMode)}
                        >
                            {drawMode
                                ? `Drawing… ${livePointCount} points`
                                : '1. Start draw path'}
                        </button>
                        <button
                            type="button"
                            disabled={!drawMode || livePointCount < 2}
                            onClick={onFinishDrawnPath}
                        >
                            2. Finish path
                        </button>
                        <button
                            type="button"
                            disabled={!drawMode || livePointCount < 1}
                            onClick={onUndoDrawPoint}
                        >
                            Undo last click
                        </button>
                        <button
                            type="button"
                            disabled={!pathSamples?.length}
                            onClick={onPlaceMarkersOnPath}
                        >
                            Place markers on path
                        </button>
                        <button
                            type="button"
                            disabled={!pathSamples?.length}
                            onClick={onClearPath}
                        >
                            Clear drawn path
                        </button>
                    </div>

                    {drawMode ? (
                        <p className="dv2-journey-dev__draw-hint">
                            You must see a bright green border on the My Goals
                            card. Click several points from the bottom of the
                            mountain up to the summit. Cyan dots should appear.
                            Then press Finish path.
                        </p>
                    ) : null}

                    {drawStatus ? (
                        <p className="dv2-journey-dev__draw-status">
                            {drawStatus}
                        </p>
                    ) : null}

                    <dl className="dv2-journey-dev__meta">
                        <div>
                            <dt>Breakpoint</dt>
                            <dd>
                                {bp.label}
                                {dirty ? ' · edited' : ''}
                            </dd>
                        </div>
                        <div>
                            <dt>Viewport</dt>
                            <dd>{viewportWidth}px</dd>
                        </div>
                        <div>
                            <dt>Card</dt>
                            <dd>
                                {cardSize?.w || '—'}×{cardSize?.h || '—'}px
                            </dd>
                        </div>
                        <div>
                            <dt>Path samples</dt>
                            <dd>
                                {pathSamples?.length
                                    ? `${pathSamples.length} points`
                                    : 'from markers'}
                            </dd>
                        </div>
                    </dl>

                    <div className="dv2-journey-dev__table-wrap">
                        <table className="dv2-journey-dev__table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>x%</th>
                                    <th>y%</th>
                                    <th>side</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {(points || []).map((p) => (
                                    <tr key={p.rank}>
                                        <td>{p.rank}</td>
                                        <td>{p.x.toFixed(1)}</td>
                                        <td>{p.y.toFixed(1)}</td>
                                        <td>
                                            <select
                                                value={p.side || 'right'}
                                                onChange={(e) =>
                                                    onSideChange(
                                                        p.rank,
                                                        e.target.value
                                                    )
                                                }
                                                aria-label={`Label side for goal ${p.rank}`}
                                            >
                                                <option value="right">
                                                    right
                                                </option>
                                                <option value="left">
                                                    left
                                                </option>
                                            </select>
                                        </td>
                                        <td className="dv2-journey-dev__nudge">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    onPointNudge(
                                                        p.rank,
                                                        -0.5,
                                                        0
                                                    )
                                                }
                                            >
                                                ←
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    onPointNudge(
                                                        p.rank,
                                                        0.5,
                                                        0
                                                    )
                                                }
                                            >
                                                →
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    onPointNudge(
                                                        p.rank,
                                                        0,
                                                        -0.5
                                                    )
                                                }
                                            >
                                                ↑
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    onPointNudge(
                                                        p.rank,
                                                        0,
                                                        0.5
                                                    )
                                                }
                                            >
                                                ↓
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="dv2-journey-dev__actions">
                        <button
                            type="button"
                            onClick={() => copy('bp', currentSnippet)}
                        >
                            Copy {breakpointId} snippet
                        </button>
                        <button
                            type="button"
                            onClick={() => copy('all', exportText)}
                            className="dv2-journey-dev__draw-btn"
                        >
                            3. Copy all breakpoints JSON
                        </button>
                        <button type="button" onClick={onResetBreakpoint}>
                            Reset {breakpointId}
                        </button>
                        <button type="button" onClick={onResetAll}>
                            Reset all
                        </button>
                    </div>

                    {copied ? (
                        <p className="dv2-journey-dev__copied">
                            {copied === 'failed'
                                ? 'Copy failed'
                                : 'Copied — paste to the agent when finished'}
                        </p>
                    ) : null}

                    <pre className="dv2-journey-dev__preview">{currentSnippet}</pre>
                </div>
            ) : null}
        </aside>
    );
}
